import {
  BarChart,
  CurrencyValue,
  ChartSkeleton,
  assetNameToRgba
} from '@blockanalitica/ui';
import { ChartConfig } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ReactNode, useMemo, useCallback } from 'react';

export interface AssetData {
  debt_underlying_symbol: string;
  amount_usd: string;
}

export default function AssetLiquidationsGraph({
  data,
  timeFilter,
  ...rest
}: {
  data: AssetData[];
  timeFilter?: ReactNode;
}) {
  const datasets = useMemo(() => {
    if (!data) return [];

    return [
      {
        xField: 'debt_underlying_symbol',
        yField: 'amount_usd',
        label: 'Assets',
        backgroundColor: data.map(
          (asset) => assetNameToRgba(asset.debt_underlying_symbol) || '#fff'
        )
      }
    ];
  }, [data]);

  const xScaleValueFormatter = useCallback(
    (value: string | number) => {
      return data[Number(value)].debt_underlying_symbol;
    },
    [data]
  );

  const subValueFormatter = useCallback((value: string | number) => {
    return <>Asset: {value}</>;
  }, []);

  const valueFormatter = useCallback((value: string | number) => {
    return (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{
          currency: 'USD'
        }}
      />
    );
  }, []);

  const config: ChartConfig = useMemo(() => {
    return {
      options: {
        scales: {
          x: {
            type: 'category'
          }
        }
      }
    };
  }, []);

  if (!data) {
    return <ChartSkeleton />;
  }

  return (
    <BarChart
      data={data as unknown as SimpleChartData[]}
      datasets={datasets}
      config={config}
      xScaleValueFormatter={xScaleValueFormatter}
      subValueFormatter={subValueFormatter}
      valueFormatter={valueFormatter}
      timeFilter={timeFilter}
      defaultDataIndex={0}
      {...rest}
    />
  );
}
